import { DatePicker } from '@waves/date-picker';
import { Popover } from '@waves/popover';
import moment from 'moment';
import React, {
  ReactElement,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { StyleSheet, View } from 'react-native';

import Colors from '../../theming/Colors';
import KitInputValidationMessage from '../KitInputValidationMessage';
import { KitDatePickerInputProps, KitDatePickerInputRefType } from './types';

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'waves-date-picker': {
        value: string | undefined;
        ref: React.RefObject<unknown>;
        placement?:
          | 'top'
          | 'top-start'
          | 'top-end'
          | 'right'
          | 'right-start'
          | 'right-end'
          | 'bottom'
          | 'bottom-start'
          | 'bottom-end'
          | 'left'
          | 'left-start'
          | 'left-end';
        disabled?: boolean;
        'max-date'?: string | undefined;
        'min-date'?: string | undefined;
        theme?: 'default' | 'dark';
        testID?: string | undefined;
      };
    }
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (!window.customElements.get('waves-popover')) {
    window.customElements.define('waves-popover', Popover);
  }

  if (!window.customElements.get('waves-date-picker')) {
    window.customElements.define('waves-date-picker', DatePicker);
  }
}

const KitDatePickerInput = forwardRef<
  KitDatePickerInputRefType,
  KitDatePickerInputProps
>((props, ref): ReactElement => {
  const {
    date, //string (mm/dd/yyyy)
    disabled, // default false
    minimumDate, //string (mm/dd/yyyy)
    maximumDate, //string (mm/dd/yyyy)
    theme = 'default', // 'default' or 'dark'
    placement = 'bottom-start',
    onChange,
    testID,
    errorLabel,
    validationStyle,
    preventInput = true,
  } = props;

  const wavesDatePickerRef = useRef<HTMLElement>(null);

  const onChangeDate = useCallback(
    (event: Event) => {
      if (onChange) {
        // @ts-ignore
        onChange(event, event.detail.date);
      }
    },
    [onChange]
  );

  useEffect(() => {
    const wavesDatePicker = wavesDatePickerRef.current;

    if (wavesDatePicker) {
      // Attach the event listener to the HTML element
      wavesDatePicker.addEventListener('change', onChangeDate);
      const inputElement = wavesDatePicker.shadowRoot?.querySelector('input');

      if (inputElement) {
        inputElement.style.border = errorLabel ? 'none' : '';
      }
    }

    // Cleanup function to remove the event listener
    return () => {
      if (wavesDatePicker) {
        wavesDatePicker.removeEventListener('change', onChangeDate);
      }
    };
  }, [wavesDatePickerRef, onChangeDate, errorLabel]);

  return (
    <>
      <KitInputValidationMessage
        message={errorLabel ? errorLabel : null}
        testID='time-picker-validation-message'
        messageStyle={validationStyle}
      >
        <View style={errorLabel ? styles.errorBorder : null}>
          <waves-date-picker
            ref={wavesDatePickerRef}
            // TODO: implement locale-aware value
            value={moment(date).format('MM/DD/YYYY')}
            placement={placement}
            disabled={disabled}
            max-date={
              maximumDate ? moment(maximumDate).format('MM/DD/YYYY') : undefined
            }
            min-date={
              minimumDate ? moment(minimumDate).format('MM/DD/YYYY') : undefined
            }
            theme={theme}
            testID={testID}
            {...(preventInput && { 'prevent-input': true })}
          />
        </View>
      </KitInputValidationMessage>
    </>
  );
});

const styles = StyleSheet.create({
  errorBorder: {
    borderColor: Colors.R500,
    borderWidth: 2,
    borderRadius: 4,
  },
});

export default KitDatePickerInput;
